import React, { useEffect, useState } from 'react';
import { ColorRing } from 'react-loader-spinner';
import Pagination from '@mui/material/Pagination';
import ListHeader from '../../../../components/ListHeader/NoCardView/index';
import ServiceCompanyEntryForm from './ServiceCompaniesEntryForm';
import ViewServiceCompanyModal from './ServiceCompaniesViewModal';
import API_BASE_URL from '../../../../Constants';

// Define the columns you want in the table
const ServiceCompanyListFields = [
  { key: 'companyName', label: 'Company Name' },
  { key: 'type', label: 'Type' },
  { key: 'status', label: 'Status' },
  { key: 'mainPhone', label: 'Main Phone' },
];

export default function ServiceCompanyList() {
  const [serviceCompanies, setServiceCompanies] = useState([]);
  const [isLoading, setIsLoading] = useState(false);

  // This is for the “Search” input
  const [searchQuery, setSearchQuery] = useState('');

  // We keep the mode in state, defaulting to 'table' 
  const [viewMode, setViewMode] = useState('table');

  // Create / View modals
  const [isCreateOpen, setIsCreateOpen] = useState(false);
  const [isViewOpen, setIsViewOpen] = useState(false);
  const [selectedCompany, setSelectedCompany] = useState(null);
  const [selectedIndex, setSelectedIndex] = useState(-1);

  // Sorting
  const [sortConfig, setSortConfig] = useState({ key: null, direction: null });

  // Pagination
  // Default 25 rows per page
  const [currentPage, setCurrentPage] = useState(1);
  const [rowsPerPage, setRowsPerPage] = useState(25);

  // --------------------------------------------------
  // 1) Fetch data from the server
  // --------------------------------------------------
  async function fetchServiceCompanies(query = searchQuery) {
    try {
      setIsLoading(true);

      // If you want server-side searching, pass ?q= to your endpoint 
      const queryString = query ? `?q=${encodeURIComponent(query)}` : '';
      const url = `${API_BASE_URL}/api/inspections/service-companies${queryString}`;

      const response = await fetch(url, { credentials: 'include' });
      if (!response.ok) {
        throw new Error(`Fetch failed, status: ${response.status}`);
      }
      const data = await response.json();
      const arr = Array.isArray(data) ? data : [];
      setServiceCompanies(arr);

      // Reset to first page after a new fetch
      setCurrentPage(1);
    } catch (error) {
      console.error('Failed to load service companies =>', error);
    } finally {
      setIsLoading(false);
    }
  }

  useEffect(() => {
    fetchServiceCompanies();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // --------------------------------------------------
  // 2) Sorting logic
  // --------------------------------------------------
  useEffect(() => {
    if (!sortConfig.key) return; // no sorting if no key yet
    const sorted = [...serviceCompanies].sort((a, b) => {
      const aVal = a[sortConfig.key] ?? '';
      const bVal = b[sortConfig.key] ?? '';

      // Numeric vs. string check
      const aIsNum = !isNaN(aVal);
      const bIsNum = !isNaN(bVal);

      if (aIsNum && bIsNum) {
        return sortConfig.direction === 'ascending'
          ? Number(aVal) - Number(bVal)
          : Number(bVal) - Number(aVal);
      }

      // string comparison
      if (aVal < bVal) return sortConfig.direction === 'ascending' ? -1 : 1;
      if (aVal > bVal) return sortConfig.direction === 'ascending' ? 1 : -1;
      return 0;
    });
    setServiceCompanies(sorted);
    setCurrentPage(1);
  }, [sortConfig, serviceCompanies]);

  function handleSort(key) {
    let direction = 'ascending';
    // If already sorted ascending, flip to descending
    if (sortConfig.key === key && sortConfig.direction === 'ascending') {
      direction = 'descending';
    }
    setSortConfig({ key, direction });
  }

  // --------------------------------------------------
  // 3) Searching
  // --------------------------------------------------
  function handleSearch() {
    // re-fetch with the current searchQuery
    fetchServiceCompanies(searchQuery);
  }

  // --------------------------------------------------
  // 4) Creating
  // --------------------------------------------------
  function handleOpenCreateModal() {
    setIsCreateOpen(true);
  }
  function handleCloseCreateModal() {
    setIsCreateOpen(false);
  }
  function refreshCompanies() {
    // re-fetch the list after a create or update
    fetchServiceCompanies(searchQuery);
  }

  // --------------------------------------------------
  // 5) Viewing
  // --------------------------------------------------
  function handleOpenView(co, index) {
    setSelectedIndex(index);
    setSelectedCompany(co);
    setIsViewOpen(true);
  }
  function handleCloseView() {
    setSelectedIndex(-1);
    setSelectedCompany(null);
    setIsViewOpen(false);
  }

  // Next/Prev inside the modal
  const hasPrev = selectedIndex > 0;
  const hasNext = selectedIndex < serviceCompanies.length - 1;
  function handlePrev() {
    if (hasPrev) {
      const newIndex = selectedIndex - 1;
      setSelectedIndex(newIndex);
      setSelectedCompany(serviceCompanies[newIndex]);
    }
  }
  function handleNext() {
    if (hasNext) {
      const newIndex = selectedIndex + 1;
      setSelectedIndex(newIndex);
      setSelectedCompany(serviceCompanies[newIndex]);
    }
  }

  function mapValue(val) {
    // If null or undefined, return 'N/A'
    if (val == null) {
      return 'N/A';
    }
    // If it's an object (like {id, typeName}), return the typeName (or any field you want)
    if (typeof val === 'object') {
      return val.typeName ?? 'N/A';
    }
    // Otherwise, just return the value itself
    return val;
  }
  

  // --------------------------------------------------
  // 6) Client-side Pagination
  // --------------------------------------------------
  const totalItems = serviceCompanies.length;
  const totalPages = Math.ceil(totalItems / rowsPerPage);

  const startIndex = (currentPage - 1) * rowsPerPage;
  const endIndex = startIndex + rowsPerPage;
  const displayedCompanies = serviceCompanies.slice(startIndex, endIndex);

  const handlePageChange = (event, newPage) => {
    setCurrentPage(newPage);
  };

  const handleRowsPerPageChange = (e) => {
    setRowsPerPage(Number(e.target.value));
    setCurrentPage(1);
  };

  return (
    <div className="mx-auto pl-4 pr-4 w-full h-full pt-4">
      {/* ListHeader has toggles for card/table => we pass them our state */}
      <ListHeader
        title="Service Companies"
        searchQuery={searchQuery}
        handleSearch={handleSearch}
        handleSearchChange={(e) => setSearchQuery(e.target.value)}
        viewMode={viewMode}
        handleViewModeChange={setViewMode}
        handleOpenCreateModal={handleOpenCreateModal}
      />

      {/* LOADING indicator */}
      {isLoading && (
        <div className="flex justify-center items-center pt-4">
          <ColorRing
            visible
            height="200"
            width="200"
            colors={['#033B5C', '#033B5C', '#033B5C', '#033B5C', '#033B5C']}
          />
        </div>
      )}

      {/* TABLE mode */}
      {!isLoading && viewMode === 'table' && (
        <table className="min-w-full bg-white rounded-lg shadow-lg overflow-hidden border-2 border-gray-200 mt-3">
          <thead className="bg-gray-200">
            <tr>
              <th className="px-4 py-2 text-left font-bold text-sss-blue-sel whitespace-nowrap">
                Actions
              </th>
              {ServiceCompanyListFields.map((field) => (
                <th
                  key={field.key}
                  className="px-4 py-2 text-left font-bold text-sss-blue-sel cursor-pointer whitespace-nowrap"
                  onClick={() => handleSort(field.key)}
                >
                  {field.label}
                </th>
              ))}
            </tr>
          </thead>
          <tbody className="text-gray-700">
            {displayedCompanies.map((co, index) => (
              <tr
                key={co.serviceCompanyId ?? index}
                className="border-t hover:bg-gray-50 transition"
              >
                <td className="px-4 py-2 whitespace-nowrap">
                  <button
                    onClick={() => handleOpenView(co, index + startIndex)}
                    className="text-sss-info font-semibold hover:underline"
                  >
                    View
                  </button>
                </td>
                {ServiceCompanyListFields.map((field) => (
                  <td key={field.key} className="px-4 py-2 whitespace-nowrap">
                    {mapValue(co[field.key])}
                  </td>
                ))}
              </tr>
            ))}
          </tbody>
        </table>
      )}

      {/* CARD mode */}
      {!isLoading && viewMode === 'card' && (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 p-4">
          {displayedCompanies.map((co, index) => (
            <div
              key={co.serviceCompanyId ?? index}
              className="p-4 bg-white rounded-lg shadow-md border-2 border-gray-200 flex flex-col"
            >
              {/* Example fields displayed in a "card" style */}
              <h3 className="text-md font-bold mb-2">Service Co #{co.serviceCompanyId || 'N/A'}</h3>
              <p>
                <strong>Company:</strong> {mapValue(co.companyName)}
              </p>
              <p>
                <strong>Type:</strong> {mapValue(co.type)}
              </p>
              <p>
                <strong>Status:</strong> {mapValue(co.status)}
              </p>
              <p>
                <strong>Main Phone:</strong> {mapValue(co.mainPhone)}
              </p>

              <button
                className="mt-2 text-sss-info font-semibold hover:underline self-start"
                onClick={() => handleOpenView(co, index + startIndex)}
              >
                View
              </button>
            </div>
          ))}
        </div>
      )}

      {/* PAGINATION FOOTER */}
      <div className="flex justify-between items-center p-4 border-t border-gray-300 bg-white">
        <Pagination
          count={totalPages}
          page={currentPage}
          onChange={handlePageChange}
          color="primary"
          shape="rounded"
        />
        <div className="flex items-center space-x-2">
          <span className="font-semibold text-gray-700">Rows per page:</span>
          <select
            className="border border-gray-300 rounded px-2 py-1 focus:outline-none"
            value={rowsPerPage}
            onChange={handleRowsPerPageChange}
          >
            <option value={5}>5</option>
            <option value={10}>10</option>
            <option value={25}>25</option>
            <option value={50}>50</option>
          </select>
        </div>
      </div>

      {/* CREATE MODAL */}
      {isCreateOpen && (
        <ServiceCompanyEntryForm
          open={isCreateOpen}
          onClose={handleCloseCreateModal}
          refreshCompanies={refreshCompanies}
        />
      )}

      {/* VIEW MODAL */}
      {isViewOpen && selectedCompany && (
        <ViewServiceCompanyModal
          open={isViewOpen}
          onClose={handleCloseView}
          company={selectedCompany}
          hasPrev={selectedIndex > 0}
          hasNext={selectedIndex < serviceCompanies.length - 1}
          setPrev={handlePrev}
          setNext={handleNext}
        />
      )}
    </div>
  );
}
